import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import {
  Save, ArrowLeft, Shield, Lock, Edit3, PlusSquare, Trash2,
  Package, ShoppingCart, Layout, Wrench, Users, MapPin, Gauge, Building2, Menu, Network, Workflow, BarChart, AlertCircle, UserCheck, FileCheck, Blocks
} from 'lucide-react';
import AuditHistoryModal from './AuditHistoryModal';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';
import api from './axiosConfig';
import { resolveIcon } from './iconResolver';

const MODULE_GROUPS = [
  {
    group: "Core Security",
    modules: [
      { id: 'users', label: 'Users & Security', icon: Lock },
      { id: 'roles', label: 'Roles Management', icon: Shield },
      { id: 'dashboard', label: 'Dashboard', icon: BarChart },
    ]
  },
  {
    group: "Inventory & Supply Chain",
    modules: [
      { id: 'items', label: 'Item Master', icon: Package },
      { id: 'tools', label: 'Tools', icon: Package },
      { id: 'inventory', label: 'Inventory (Balances)', icon: Layout },
      { id: 'invusage', label: 'Inventory Usage', icon: Layout },
      { id: 'storerooms', label: 'Storerooms', icon: MapPin },
      { id: 'receiving', label: 'Receiving', icon: ShoppingCart },
    ]
  },
  {
    group: "Purchasing",
    modules: [
      { id: 'pr', label: 'Purchase Requisitions (PR)', icon: ShoppingCart },
      { id: 'po', label: 'Purchase Orders (PO)', icon: ShoppingCart },
      { id: 'rfq', label: 'Request fro Quotation (RFQ)', icon: ShoppingCart },
      { id: 'mr', label: 'Material Requests (MR)', icon: ShoppingCart },
      { id: 'invoices', label: 'Purchase Invoices', icon: ShoppingCart },
      { id: 'vendors', label: 'Vendors', icon: UserCheck },
    ]
  },
  {
    group: "Work & Assets",
    modules: [
      { id: 'assets', label: 'Assets', icon: Shield },
      { id: 'locations', label: 'Locations', icon: MapPin },
      { id: 'workorders', label: 'Work Orders', icon: Edit3 },
      { id: 'pm', label: 'Preventive Maintenance', icon: Wrench },
      { id: 'jp', label: 'Job Plans', icon: Wrench },
      { id: 'meters', label: 'Meters', icon: Gauge },
      { id: 'asset-categories', label: 'Asset Category', icon: Layout},
      { id: 'failure-codes', label: 'Failure Code', icon: Shield},
    ]
  },
  {
    group: "Contracts",
    modules: [
      { id: 'contracts', label: 'Contracts', icon: FileCheck },
      { id: 'sla', label: 'SLA', icon: FileCheck },
    ]
  }, 
  {
    group: "Service Requests",
    modules: [
      { id: 'sr', label: 'Service Requests', icon: ShoppingCart },
    ]
  },  
  {
    group: "Incidents",
    modules: [
      { id: 'incidents', label: 'Incident', icon: AlertCircle },
    ]
  },   
  {
    group: "Resources",
    modules: [
      { id: 'labor', label: 'Labor', icon: Users },
      { id: 'craft', label: 'Crafts', icon: Wrench },
      { id: 'person', label: 'Person', icon: UserCheck },
    ]
  },
  {
    group: "Administration",
    modules: [
      { id: 'organizations', label: 'Organizations', icon: Building2 },
      { id: 'domains', label: 'Domains', icon: Menu },
      { id: 'classifications', label: 'Classifications', icon: Network },
      { id: 'cobiemanager', label: 'Cobie Manager', icon: Layout },
      { id: 'workflow', label: 'Workflow', icon: Workflow },
      { id: 'uniimporter', label: 'Universal Import', icon: PlusSquare },
      { id: 'admin/workflow/studio', label: 'Action Script', icon: Workflow },
      { id: 'custom_apps', label: 'Custom App Builder', icon: Blocks },
    ]
  }
];

// 🎯 بارامتر groups بدل الاعتماد على MODULE_GROUPS الثابتة مباشرة، عشان
// نقدر نحسبها كمان لمجموعة التطبيقات المخصصة الديناميكية تحت
const generateDefaultPermissions = (groups) => {
  const perms = {};
  groups.forEach(group => {
    group.modules.forEach(mod => {
      perms[mod.id] = { read: false, create: false, update: false, delete: false };
    });
  });
  return perms;
};

const RoleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new' || id === 'undefined';
  const [loading, setLoading] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  // 🎯 التطبيقات المخصصة بتضاف كمجموعة صلاحيات ديناميكية - لازم تكون موجودة
  // في MODULE_GROUPS الفعلية اللي بترسم بيها الشاشة وفي الـ default permissions
  // قبل ما اليوزر يضغط أي checkbox، وإلا handlePermissionChange هيحاول يعدل
  // على مفتاح مش موجود ويوقع
  const [customModels, setCustomModels] = useState([]);
  useEffect(() => {
    api.get('/custom-models', { params: { published_only: false } })
      .then(res => setCustomModels(res.data || []))
      .catch(() => setCustomModels([]));
  }, []);

  const allModuleGroups = React.useMemo(() => {
    if (customModels.length === 0) return MODULE_GROUPS;
    return [
      ...MODULE_GROUPS,
      {
        group: "Custom Applications",
        modules: customModels.map(m => ({ id: m.module_key, label: m.label, icon: resolveIcon(m.icon_name) })),
      },
    ];
  }, [customModels]);

  const defaultPermissions = React.useMemo(() => generateDefaultPermissions(allModuleGroups), [allModuleGroups]);

  const [roleData, setRoleData] = useState({ name: '', permissions: defaultPermissions });

  // لو المودلز الكستم اتحملوا بعد ما الـ roleData اتظبط أصلاً (أو بعد ما
  // الرول الحالي اتحمل من الباك إند)، ضيف أي مفتاح جديد ناقص من غير ما تلمس
  // القيم الموجودة فعلاً
  useEffect(() => {
    setRoleData(prev => ({ ...prev, permissions: { ...defaultPermissions, ...prev.permissions } }));
  }, [defaultPermissions]);

  useEffect(() => {
    const openLog = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
  }, []);

  useEffect(() => {
    const fetchRole = async () => {
      if (isNew) return;
      setLoading(true);
      try {
        const res = await axios.get(`/api/roles/${id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        // تأكد من معالجة الصلاحيات سواء كانت String أو Object
        let perms = typeof res.data.permissions === 'string' ? JSON.parse(res.data.permissions) : res.data.permissions;
        setRoleData({
            id: res.data.id,
            name: res.data.name || res.data.role_name,
            permissions: { ...defaultPermissions, ...perms }
        });
      } catch (err) { 
        toast.error("Error loading role details"); 
      } finally { 
        setLoading(false); 
      }
    };
    fetchRole();
  }, [id, isNew]);

  const handlePermissionChange = (modId, action) => {
    setRoleData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [modId]: { ...prev.permissions[modId], [action]: !prev.permissions[modId][action] }
      }
    }));
  };

  // دالة الـ Select All للمجموعة
  const toggleGroupAction = (modules, action) => {
    const allActive = modules.every(m => roleData.permissions[m.id][action]);
    const updatedPerms = { ...roleData.permissions };
    modules.forEach(m => {
      updatedPerms[m.id][action] = !allActive;
    });
    setRoleData({ ...roleData, permissions: updatedPerms });
  };

  const handleSave = async () => {
    if (!roleData.name) return toast.error("Role Name is required");
    setLoading(true);
    try {
      // نرسل الصلاحيات كـ JSON string للباكيند
      const payload = { 
        name: roleData.name, 
        permissions: JSON.stringify(roleData.permissions) 
      };
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      
      if (isNew) {
        await axios.post('/api/roles', payload, { headers });
      } else {
        await axios.put(`/api/roles/${id}`, payload, { headers });
      }
      
      toast.success("Security Role Saved Successfully");
      navigate('/roles');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Swal.fire({
      ...swalTheme(),
      title: 'Are you sure?', text: 'This action cannot be undone!',
      icon: 'warning', showCancelButton: true, confirmButtonColor: swalDangerColor(),
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`/api/roles/${id}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          toast.success('Role deleted successfully');
          navigate('/roles');
        } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
      }
    });
  };

  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);

  return (
    <div className="flex flex-col h-full bg-surface-app font-bold uppercase tracking-tighter text-[11px]">
      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="roles" recordId={id} />
      {/* Header Toolbar */}
      <div className="bg-surface-card p-3 border-b border-line shadow-sm flex justify-between items-center sticky top-0 z-20">
        <div className="flex items-center gap-4 ml-4">
          <button onClick={() => navigate('/roles')} className="p-1 hover:bg-surface-panel rounded-full text-primary transition-colors">
            <ArrowLeft size={18}/>
          </button>
          <div className="flex flex-col">
            <span className="text-[9px] text-ink-secondary italic font-black">Security Role Config</span>
            <span className="text-primary font-black text-lg leading-tight">
                {roleData.name || 'NEW ENTERPRISE ROLE'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button
              onClick={handleDelete}
              className="p-2.5 text-status-danger hover:bg-status-danger-bg rounded-sm transition-colors"
              title="Delete Role"
            >
              <Trash2 size={16}/>
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={loading}
            className="bg-primary text-white px-10 py-2.5 rounded-sm flex items-center gap-2 hover:bg-primary-dark transition-all shadow-lg active:scale-95 disabled:opacity-50"
          >
            <Save size={16} /> {loading ? 'PROCESSING...' : 'SAVE ROLE'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-8 max-w-6xl mx-auto w-full space-y-8">
        {/* Role Name Input */}
        <div className="bg-surface-card border-2 border-line p-6 rounded-sm shadow-sm">
          <label className="text-[9px] text-ink-secondary block mb-1 tracking-widest">ROLE IDENTIFIER</label>
          <input
            className="w-full border-b-2 border-primary outline-none p-2 text-xl font-black text-primary uppercase placeholder-ink-secondary/40"
            placeholder="E.G. SR_CLERK"
            value={roleData.name}
            onChange={(e) => setRoleData({...roleData, name: e.target.value.toUpperCase()})}
          />
        </div>

        {/* Permissions Groups */}
        {allModuleGroups.map((group, gIdx) => (
          <div key={gIdx} className="bg-surface-card border-2 border-line rounded-sm shadow-sm overflow-hidden">
            <div className="bg-primary-dark text-white p-3 text-[10px] tracking-widest flex items-center justify-between font-black border-b border-white/10">
              <span className="flex items-center gap-2 text-sky">
                <Shield size={14} className="text-status-warning"/> {group.group}
              </span>
              <div className="flex gap-4 items-center">
                  <span className="text-[8px] text-white/60 mr-2 italic">QUICK TOGGLE:</span>
                  {['read', 'create', 'update', 'delete'].map(action => (
                    <button
                      key={action}
                      onClick={() => toggleGroupAction(group.modules, action)}
                      className="hover:text-status-warning transition-colors cursor-pointer border border-white/20 px-1.5 py-0.5 rounded uppercase text-[8px]"
                    >
                      All {action}
                    </button>
                  ))}
              </div>
            </div>
            <table className="w-full text-left">
              <thead className="bg-surface-panel text-[9px] text-ink-secondary border-b border-line">
                <tr>
                  <th className="p-4 w-2/5 tracking-widest">Application / Module</th>
                  {['READ', 'CREATE', 'UPDATE', 'DELETE'].map(a => (
                    <th key={a} className="p-4 text-center border-l border-line w-20 tracking-widest">{a}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line-subtle">
                {group.modules.map((mod) => (
                  <tr key={mod.id} className="hover:bg-primary-soft/30 group transition-colors">
                    <td className="p-4 flex items-center gap-3 font-black text-primary">
                      <mod.icon size={16} className="text-line group-hover:text-primary transition-colors" />
                      {mod.label}
                    </td>
                    {['read', 'create', 'update', 'delete'].map(action => (
                      <td key={action} className="p-4 text-center border-l border-line-subtle">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-primary cursor-pointer shadow-sm"
                          checked={roleData.permissions?.[mod.id]?.[action] || false}
                          onChange={() => handlePermissionChange(mod.id, action)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RoleDetails;